/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Tenant, UserSignUpMethod } from '@touchplan/common';
import { AuthService } from 'src/app/services/auth.service';
import { from, EMPTY } from 'rxjs';
import { AppState } from 'src/app/services/app-state.service';
import { take } from 'rxjs/operators';

//base version in root is _probably_ fine?
@Injectable({ providedIn: 'root' })
export class AuthOutputs {
	private authService = inject(AuthService);
	protected appState = inject(AppState);
	protected router = inject(Router);

	public finishLogin(tenant: Tenant, currentRoute: ActivatedRoute) {
		if (tenant && tenant.loaded && !tenant.isDefaultTenant && tenant.tenantMetadata?.providerId) {
			console.log('you are sso');
			this.authService.signInWithRedirect(tenant.tenantMetadata.providerId);
		} else {
			console.log('you are not sso, banished to the normal login page');

			this.finishLoginNotSSO(currentRoute);
		}
	}

	public finishLoginNotSSO(currentRoute: ActivatedRoute) {
		this.router.navigate(['../login2'], {
			relativeTo: currentRoute,
			queryParamsHandling: 'preserve',
		});
	}

	public finishLoginWithEmailAndPassword(redirectTo?: string) {
		//this redirect doesn't play nice with partially created users since it triggers
		//leaving "auth" flow, which nukes the accountCreationStore, and we _really_ want that to still be there
		//the simplest solution is probably to wait for user data and eagerly redirect to the signup page without
		//the resolver
		this.appState.waitForUser$.pipe(take(1)).subscribe((user) => {
			if (user.method === UserSignUpMethod.AuthenticationHandler) {
				this.router.navigate(['/auth/signup']);
			} else {
				const redirect = redirectTo || '/projects';
				this.router.navigate([redirect]);
			}
		});
	}

	public forgotPasswordRedirect(currentRoute: ActivatedRoute) {
		this.router.navigate(['../reset-password'], {
			relativeTo: currentRoute,
			queryParamsHandling: 'preserve',
		});
	}

	public attemptSignupReauth(tenant: Tenant) {
		if (
			(tenant.tenantMetadata as any)?.providerId &&
			(tenant.tenantMetadata as any)?.providerId !== 'password'
		) {
			const providerId = (tenant.tenantMetadata as any).providerId;
			return from(this.authService.signInWithRedirect(providerId));
		} else {
			this.router.navigate(['/auth']);
			return EMPTY;
		}
	}

	public finishSignup() {
		this.router.navigate(['/projects']);
	}

	public cancelSignup(currentRoute: ActivatedRoute) {
		this.router.navigate(['../login'], {
			relativeTo: currentRoute,
			queryParamsHandling: 'preserve',
		});
	}

	public continueSignUp(tenant: Tenant, currentRoute: ActivatedRoute) {
		if (tenant && tenant.loaded && !tenant.isDefaultTenant && tenant.tenantMetadata?.providerId) {
			console.log('you are sso');
			this.authService.signInWithRedirect(tenant.tenantMetadata.providerId);
		} else {
			console.log('you are not sso, continue to the normal sign up page');

			this.router.navigate(['../signup'], {
				relativeTo: currentRoute,
				queryParamsHandling: 'preserve',
			});
		}
	}
}
